import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import { motion } from 'framer-motion';
import { FaArrowRight, FaExternalLinkAlt, FaTwitter, FaInstagram, FaBehance } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, onSnapshot } from 'firebase/firestore';
import client2 from '../images/client2.png';

// Hardcoded projects that always appear (after Firebase projects)
const hardcodedProjects = [
  {
    id: 'hardcoded-1',
    title: "1Africa E-Commerce Website",
    category: "E-commerce Solution | 2024",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1200&h=675",
    link: "https://1africashop.co.za/",
    description: "This fully functional e-commerce platform offers seamless browsing and secure payment integration. Our goal was to ensure a flawless user experience while maximizing the brand's online presence and sales."
  },
  {
    id: 'hardcoded-2',
    title: "CapitaVista",
    category: "Website Development | 2023",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&h=675",
    link: "https://capitavista.framer.website/",
    description: "An investment website designed to empower clients with detailed insights into their personal investment portfolios. The site integrates advanced data tracking tools and a user-friendly interface."
  },
  {
    id: 'hardcoded-3',
    title: "Ahead Travel App",
    category: "Mobile App Design",
    image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&h=675",
    link: "https://www.behance.net/gallery/206261541/Ahead-travel-app",
    description: "A sleek mobile app designed to simplify travel planning. The app focuses on providing an intuitive user experience with advanced features such as live travel updates, personalized recommendations, and seamless bookings."
  },
  {
    id: 'hardcoded-4',
    title: "Park Mobile App",
    category: "UI/UX Design",
    image: client2,
    link: "https://www.behance.net/gallery/203853089/PARK-Mobile-App",
    description: "The Park mobile app provides users with a simple and efficient way to find and pay for parking spaces. We focused on creating an accessible and smooth user interface that reduces friction in the parking experience.",
    isFeatured: true
  },
  {
    id: 'hardcoded-5',
    title: "All Paid Fintech App",
    category: "Fintech App | UI/UX Design",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&h=675",
    link: "https://www.behance.net/gallery/203777297/All-Paid-Bill-Pay-app",
    description: "A user-centric mobile application designed to simplify bill payments and financial management. The app offers a seamless, secure, and intuitive experience for users to handle their finances with ease."
  }
];

const Work = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const projectsCollection = collection(db, 'projects');
    
    const unsubscribe = onSnapshot(projectsCollection, (snapshot) => {
      const firebaseProjects = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      
      // Sort Firebase projects by createdAt (newest first), with fallback to order
      const sortedFirebaseProjects = firebaseProjects.sort((a, b) => {
        // Prioritize createdAt for sorting (newest first)
        if (a.createdAt && b.createdAt) {
          return new Date(b.createdAt) - new Date(a.createdAt);
        }
        if (a.createdAt && !b.createdAt) return -1; // a comes first
        if (!a.createdAt && b.createdAt) return 1; // b comes first
        // Fallback to order if createdAt doesn't exist
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return 0;
      });
      
      // Combine: Firebase projects first (newest first), then hardcoded projects
      setProjects([...sortedFirebaseProjects, ...hardcodedProjects]);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching projects:', error);
      // On error, just show hardcoded projects
      setProjects(hardcodedProjects);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-perspective-carbon text-perspective-sand relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute inset-0 bg-grid-soft [background-size:28px_28px] opacity-20" />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="h-16" />

        {/* Hero Section */}
        <section className="px-4 pt-12 pb-12 lg:pt-16 lg:pb-16">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em] mb-4">Our portfolio</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Featured Projects
              </h1>
              <p className="text-lg sm:text-xl text-perspective-cloud/85 max-w-3xl mx-auto leading-relaxed">
                Explore our portfolio of successful projects that showcase our expertise in digital innovation, user-centered design, and technical excellence.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Featured Projects Grid */}
        <section className="px-4 py-16 lg:py-20">
          <div className="max-w-6xl mx-auto">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-perspective-sage border-t-transparent"></div>
                <p className="text-perspective-cloud/80 mt-4">Loading projects...</p>
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-perspective-cloud/80 text-lg">No projects available yet.</p>
              </div>
            ) : (
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {projects.map((project, index) => (
                  <ProjectCard key={project.id || index} {...project} />
                ))}
              </motion.div>
            )}
          </div>
        </section>

        {/* Contact CTA */}
        <section className="px-4 py-16 lg:py-20">
          <div className="max-w-6xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-r from-perspective-carbon to-perspective-border text-white shadow-elevated border border-perspective-sage/15">
            <div className="p-10 md:p-12 text-center">
              <p className="text-sm uppercase tracking-[0.12em] text-perspective-mint mb-4">Let's work together</p>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">Start Your Project With Us</h2>
              <p className="text-lg text-perspective-mint/90 mb-8 max-w-2xl mx-auto">
                Let's bring your vision to life with our expertise and passion for digital excellence.
              </p>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-perspective-sage text-perspective-carbon font-semibold hover:shadow-xl transition-all duration-300 shadow-lg"
                onClick={() => navigate('/contact')}
              >
                Get In Touch
                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-perspective-carbon text-white border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-14">
            <div className="grid md:grid-cols-4 gap-8 mb-10">
              <div className="md:col-span-2">
                <img 
                  src="/perspectivelogo.png" 
                  alt="Perspective HQ" 
                  className="h-8 w-auto mb-4"
                />
                <p className="text-perspective-mint/90 max-w-xl mb-6">
                  Creating digital experiences that drive growth and innovation. We're passionate about building solutions that make a difference.
                </p>
                <div className="flex gap-3">
                  <a href="https://twitter.com/perspectivehq" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaTwitter size={16} />
                  </a>
                  <a href="https://instagram.com/theperspectivehq" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaInstagram size={16} />
                  </a>
                  <a href="https://www.behance.net/skaina" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaBehance size={16} />
                  </a>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Navigate</h4>
                <div className="space-y-2 text-perspective-mint/90">
                  <button onClick={() => navigate('/work')} className="block hover:text-perspective-sage transition-colors">Work</button>
                  <button onClick={() => navigate('/services')} className="block hover:text-perspective-sage transition-colors">Services</button>
                  <button onClick={() => navigate('/about')} className="block hover:text-perspective-sage transition-colors">About</button>
                  <button onClick={() => navigate('/blog')} className="block hover:text-perspective-sage transition-colors">Blog</button>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Contact</h4>
                <div className="space-y-2 text-perspective-mint/90">
                  <p>info@perspectivehq.co.za</p>
                  <p>+27 69 339 5619</p>
                  <p>Durban, South Africa</p>
                </div>
              </div>
            </div>
            <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-sm text-perspective-mint/80">
              <p>© 2021-2025 Perspective HQ. All rights reserved.</p>
              <div className="flex gap-4">
                <button onClick={() => navigate('/privacy-policy')} className="hover:text-perspective-sage transition-colors">Privacy Policy</button>
                <button onClick={() => navigate('/terms-of-service')} className="hover:text-perspective-sage transition-colors">Terms of Service</button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

const ProjectCard = ({ id, title, category, image, description, link }) => {
  return (
    <motion.div 
      className="group bg-white/5 backdrop-blur border border-white/10 rounded-3xl shadow-card hover:shadow-elevated transition-all duration-300 overflow-hidden"
      whileHover={{ y: -8 }}
      transition={{ duration: 0.3 }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <a 
        href={link} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="block"
      >
        <div className="relative aspect-[4/3] overflow-hidden">
          <img 
            src={image} 
            alt={title} 
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-perspective-carbon/80 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 px-6 py-3 rounded-full flex items-center gap-2 text-perspective-sage font-semibold">
                <span>View Project</span>
                <FaExternalLinkAlt className="text-sm group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>
      </a>
      <div className="p-6">
        <div className="mb-4">
          <span className="inline-block bg-perspective-sage/15 text-perspective-sage px-3 py-1 rounded-full text-sm font-medium mb-3 border border-perspective-sage/20">
            {category}
          </span>
        </div>
        <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-perspective-sage transition-colors">
          {title}
        </h3>
        <p className="text-perspective-cloud/80 leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
};

export default Work;
